if (!customElements.get('td-floating-items')) {
  class TDFloatingItems extends HTMLElement {
    static selectors = {
      checklist: '[data-td-floating-checklist]',
      keyItem: '[data-td-key-item]',
    };

    static discoveryLineRatio = 0.75;

    constructor() {
      super();
      this.handleIntersection = this.handleIntersection.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
      this.renderFrame = this.renderFrame.bind(this);
    }

    connectedCallback() {
      this.keyItems = Array.from(this.querySelectorAll(TDFloatingItems.selectors.keyItem));
      this.squeezeQuery = window.matchMedia('(min-width: 990px)');
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.isTracking = false;
      this.renderFrameId = null;
      this.editorActiveBlockId = null;

      if (this.isDesignMode || this.reduceMotionQuery.matches) {
        this.setAllKeyItemsDiscovered(true);
        return;
      }

      if (!this.keyItems.length) {
        return;
      }

      this.sectionObserver = new IntersectionObserver(this.handleIntersection, {
        root: this.getIntersectionRoot(),
        rootMargin: '20% 0px 20% 0px',
        threshold: 0,
      });
      this.sectionObserver.observe(this);

      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);

      this.updateKeyItemDiscovery();
    }

    disconnectedCallback() {
      this.stopTracking();
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);

      if (this.sectionObserver) {
        this.sectionObserver.disconnect();
        this.sectionObserver = null;
      }
    }

    getIntersectionRoot() {
      if (this.squeezeQuery.matches) {
        return document.querySelector('.page-wrapper') || null;
      }

      return null;
    }

    handleIntersection(entries) {
      const isIntersecting = entries.some((entry) => entry.isIntersecting);
      if (isIntersecting) {
        this.startTracking();
      } else {
        this.stopTracking();
      }
    }

    startTracking() {
      if (this.isTracking) {
        return;
      }

      this.isTracking = true;
      this.renderFrameId = requestAnimationFrame(this.renderFrame);
    }

    stopTracking() {
      this.isTracking = false;

      if (this.renderFrameId) {
        cancelAnimationFrame(this.renderFrameId);
        this.renderFrameId = null;
      }
    }

    renderFrame() {
      if (!this.isTracking || !this.isConnected) {
        this.renderFrameId = null;
        return;
      }

      if (!this.editorActiveBlockId) {
        this.updateKeyItemDiscovery();
      }

      this.renderFrameId = requestAnimationFrame(this.renderFrame);
    }

    getFillStagger() {
      if (this.fillStagger == null) {
        const raw = getComputedStyle(this).getPropertyValue('--td-floating-check-fill-stagger').trim();
        const parsed = raw.endsWith('ms') ? parseFloat(raw) : parseFloat(raw) * 1000;
        this.fillStagger = Number.isFinite(parsed) ? parsed : 560;
      }

      return this.fillStagger;
    }

    // Stagger check fills so each starts after the previous finishes.
    applyKeyItemStates(states) {
      const stagger = this.getFillStagger();
      const now = performance.now();
      let chainEndsAt = Math.max(this.fillChainEndsAt || 0, now);

      this.keyItems.forEach((item, itemIndex) => {
        const shouldDiscover = Boolean(states[itemIndex]);

        if (shouldDiscover === (item.dataset.tdActive === 'true')) {
          return;
        }

        if (shouldDiscover) {
          const delay = Math.max(0, chainEndsAt - now);
          item.style.setProperty('--td-floating-check-fill-delay', `${Math.round(delay)}ms`);
          chainEndsAt = now + delay + stagger;
        } else {
          item.style.removeProperty('--td-floating-check-fill-delay');
        }

        item.dataset.tdActive = shouldDiscover ? 'true' : 'false';
      });

      const hasDiscovered = this.keyItems.some((item) => item.dataset.tdActive === 'true');
      this.fillChainEndsAt = hasDiscovered ? chainEndsAt : 0;
    }

    updateKeyItemDiscovery() {
      if (!this.keyItems.length) {
        return;
      }

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
      const discoveryLine = viewportHeight * TDFloatingItems.discoveryLineRatio;

      this.applyKeyItemStates(
        this.keyItems.map((item) => item.getBoundingClientRect().top < discoveryLine)
      );
    }

    setAllKeyItemsDiscovered(isDiscovered) {
      this.applyKeyItemStates(this.keyItems.map(() => isDiscovered));
    }

    setKeyItemsDiscoveredUpTo(index) {
      this.applyKeyItemStates(this.keyItems.map((item, itemIndex) => itemIndex <= index));
    }

    handleBlockSelect(event) {
      if (!this.contains(event.target)) {
        return;
      }

      const blockId = event.detail?.blockId;
      if (!blockId) {
        return;
      }

      const keyItem = this.keyItems.find((item) => item.dataset.tdBlockId === blockId);
      if (!keyItem) {
        return;
      }

      this.editorActiveBlockId = blockId;
      this.stopTracking();

      const index = parseInt(keyItem.dataset.tdIndex || '0', 10);
      this.setKeyItemsDiscoveredUpTo(index);
    }

    handleBlockDeselect(event) {
      if (!this.contains(event.target)) {
        return;
      }

      this.editorActiveBlockId = null;
      this.updateKeyItemDiscovery();
      this.startTracking();
    }
  }

  customElements.define('td-floating-items', TDFloatingItems);
}
